/* /loans — loans & salary advances: KPIs, list, approve/reject, schedule, print voucher, new loan with validation. */
import "./pay.css";
import { useState } from "react";
import { useCreateLoan, useDecideLoan, useLoans } from "../../data/hooks";
import { loanDeduction } from "../../domain/payroll";
import type { Employee, Loan } from "../../data/types";
import { useOverlays } from "../../app/overlays";
import { useI18n } from "../../i18n";
import { fmtN, gDate, moneyText } from "../../lib/format";
import {
  Affix, Alert, Bar, Button, EmptyState, ErrorBox, Field, IconButton, Input, Kpi, Modal, Money, PageHeader, Person, Seg, SelectField, Skeleton, errMsg, useToast,
} from "../../ui";
import { StatusChip, numOf, unknownEmp, useEmpMap, useRole } from "./components/common";
import { curL } from "../../lib/country";

export const LOAN_TYPES: [string, string, string][] = [["Salary advance", "Salary advance", "سلفة راتب"], ["Personal loan", "Personal loan", "قرض شخصي"]];
const typeLabel = (t: string, isAr: boolean) => { const x = LOAN_TYPES.find((l) => l[0] === t); return x ? (isAr ? x[2] : x[1]) : t; };
export const loanMax = (e: Pick<Employee, "basic" | "housing" | "transport">) => 3 * (e.basic + e.housing + e.transport);

type F = { employeeId: string; type: string; amount: string; months: string };
export function validateLoan(f: F, e: Employee | undefined): Record<string, string> {
  const err: Record<string, string> = {};
  if (!e) err.employeeId = "required";
  if (!LOAN_TYPES.some((l) => l[0] === f.type)) err.type = "must be Salary advance or Personal loan";
  const a = numOf(f.amount), m = numOf(f.months);
  if (!(a > 0)) err.amount = "must be > 0";
  else if (e && a > loanMax(e)) err.amount = "max " + fmtN(loanMax(e)) + " (3 months of basic + housing + transport)";
  if (!Number.isInteger(m) || m < 1 || m > 60) err.months = "must be an integer between 1 and 60";
  return err;
}

function NewLoanModal({ onClose }: { onClose: () => void }) {
  const { t, L, name, lang } = useI18n();
  const toast = useToast();
  const emps = useEmpMap();
  const create = useCreateLoan();
  const [f, setF] = useState<F>({ employeeId: "", type: "Salary advance", amount: "6000", months: "6" });
  const [touched, setTouched] = useState(false);
  const [srv, setSrv] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<string | null>(null);
  const empId = f.employeeId || String(emps.list[0]?.id ?? "");
  const e = emps.map.get(Number(empId));
  const form = { ...f, employeeId: empId };
  const errs = { ...(touched ? validateLoan(form, e) : {}), ...srv };
  const set = (k: keyof F) => (v: string) => { setF((x) => ({ ...x, [k]: v })); setSrv({}); setConflict(null); setTouched(true); };
  const a = numOf(f.amount), m = numOf(f.months);

  const submit = async () => {
    setTouched(true);
    if (Object.keys(validateLoan(form, e)).length) return;
    try {
      await create.mutateAsync({ employeeId: Number(empId), type: f.type, amount: a, months: m });
      toast(L("Loan request created", "تم إنشاء طلب القرض"));
      onClose();
    } catch (err) {
      const fe = (err as { fields?: Record<string, string>; status?: number }).fields;
      if (fe && Object.keys(fe).length) setSrv(fe);
      else if ((err as { status?: number }).status === 409) setConflict(errMsg(err));
      else toast(errMsg(err), "bad");
    }
  };

  return (
    <Modal title={L("New loan", "قرض جديد")} onClose={onClose} testId="loan-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={create.isPending} data-testid="loan-submit">{t("Submit")}</Button></>}>
      <div className="stack">
        <SelectField label={t("Employee")} value={empId} onChange={set("employeeId")} options={emps.list.map((x) => [x.id, name(x) + " · " + x.code])} err={errs.employeeId} testId="loan-employee" req />
        <SelectField label={t("Type")} value={f.type} onChange={set("type")} options={LOAN_TYPES.map((l): [string, string] => [l[0], L(l[1], l[2])])} err={errs.type} testId="loan-type" req />
        <div className="form2">
          <Field label={t("Amount")} req err={errs.amount} htmlFor="ln-amt" hint={e ? L("Max ", "الحد الأقصى ") + moneyText(loanMax(e), lang, 0) : undefined}>
            <Affix pre={curL(L)}><Input id="ln-amt" className="num" type="number" min={1} value={f.amount} onChange={(ev) => set("amount")(ev.target.value)} bad={!!errs.amount} data-testid="loan-amount" /></Affix>
          </Field>
          <Field label={L("Instalments", "عدد الأقساط")} req err={errs.months} htmlFor="ln-mo" hint={L("1–60 months", "1–60 شهرًا")}>
            <Affix post={t("months")}><Input id="ln-mo" className="num" type="number" min={1} max={60} value={f.months} onChange={(ev) => set("months")(ev.target.value)} bad={!!errs.months} data-testid="loan-months" /></Affix>
          </Field>
        </div>
        {a > 0 && m >= 1 && <Alert tone="info" tint testId="loan-instalment" title={L("Monthly deduction ", "الخصم الشهري ") + moneyText(a / m, lang)} desc={L("Deducted automatically from payroll after approval. Deductions are capped at 50% of the wage (Art. 92).", "يُخصم تلقائيًا من الرواتب بعد الاعتماد. لا تتجاوز الاستقطاعات 50% من الأجر (م 92).")} />}
        {conflict && <Alert tone="bad" testId="loan-conflict" title={L("Cannot create loan", "تعذر إنشاء القرض")} desc={conflict} />}
      </div>
    </Modal>
  );
}

function ScheduleModal({ loan, emp, onClose }: { loan: Loan; emp: Employee | undefined; onClose: () => void }) {
  const { t, L, name, lang } = useI18n();
  const inst = loan.amount / loan.months;
  const rows = Array.from({ length: loan.months }, (_, i) => {
    const d = new Date(loan.date + "T12:00:00"); d.setMonth(d.getMonth() + i);
    return { i, month: d.toISOString().slice(0, 10), balance: loan.amount - inst * (i + 1), paid: i < loan.paid };
  });
  return (
    <Modal title={L("Deduction schedule", "جدول الخصم") + " · " + (emp ? name(emp) : "#" + loan.employeeId)} onClose={onClose} testId="schedule-modal" className="wide" foot={<Button onClick={onClose}>{t("Close")}</Button>}>
      <div className="tw">
        <table className="tbl compact">
          <thead><tr><th>#</th><th>{t("Month")}</th><th className="end">{L("Instalment", "القسط")}</th><th className="end">{L("Balance", "الرصيد")}</th><th>{t("Status")}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.i} data-testid={"sched-" + r.i}><td className="num">{r.i + 1}</td><td>{gDate(r.month, lang, { month: "short", year: "numeric" })}</td><td className="end"><Money value={inst} /></td><td className="end"><Money value={Math.max(0, r.balance)} /></td><td><StatusChip status={r.paid ? "Paid" : "Pending"} /></td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default function Loans() {
  const { t, L, isAr } = useI18n();
  const toast = useToast();
  const { openPrint } = useOverlays();
  const q = useLoans();
  const emps = useEmpMap();
  const decide = useDecideLoan();
  const { can } = useRole();
  const [filter, setFilter] = useState<"all" | "Pending" | "Repaying" | "Settled" | "Rejected">("all");
  const [modal, setModal] = useState<null | "new" | Loan>(null);
  const [busy, setBusy] = useState<number | null>(null);

  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const all = q.data;
  const list = all.filter((l) => filter === "all" || l.status === filter);
  const repaying = all.filter((l) => l.status === "Repaying");
  const outstanding = repaying.reduce((a, l) => a + l.amount * (1 - l.paid / l.months), 0);
  const byId = all.slice().sort((a, b) => a.id - b.id);
  const deducted = [...new Set(repaying.map((l) => l.employeeId))].reduce((a, id) => a + loanDeduction(id, byId), 0);

  const doDecide = (l: Loan, approve: boolean) => {
    setBusy(l.id);
    decide.mutate({ id: l.id, approve }, {
      onSuccess: () => toast(approve ? L("Loan approved · deductions start next payroll", "تم اعتماد القرض · يبدأ الخصم من المسير القادم") : L("Loan rejected", "تم رفض القرض"), approve ? "ok" : "bad"),
      onError: (e) => toast(errMsg(e), "bad"),
      onSettled: () => setBusy(null),
    });
  };

  return (
    <>
      <PageHeader title={t("Loans & advances")} sub={L("Instalments are deducted automatically in payroll", "تُخصم الأقساط تلقائيًا من الرواتب")}
        actions={<Button variant="pri" icon="plus" onClick={() => setModal("new")} data-testid="btn-new-loan">{L("New loan", "قرض جديد")}</Button>} />
      <div className="g g-4 mb">
        <Kpi testId="kpi-outstanding" label={L("Outstanding", "الرصيد القائم")} value={fmtN(outstanding, 0)} unit={curL(L)} icon="banknote" tone="gold" />
        <Kpi testId="kpi-active" label={L("Active loans", "قروض نشطة")} value={String(repaying.length)} icon="refresh" />
        <Kpi testId="kpi-pending" label={t("Pending")} value={String(all.filter((l) => l.status === "Pending").length)} icon="inbox" tone="warn" />
        <Kpi testId="kpi-deducted" label={L("Deducted this month", "المخصوم هذا الشهر")} value={fmtN(deducted, 0)} unit={curL(L)} icon="minus" tone="info" />
      </div>
      <section className="card">
        <div className="toolbar">
          <Seg label={t("Status")} value={filter} onChange={setFilter} items={[["all", L("All", "الكل")], ["Pending", t("Pending")], ["Repaying", t("Repaying")], ["Settled", t("Settled")], ["Rejected", t("Rejected")]]} />
        </div>
        {list.length === 0 ? <EmptyState title={L("No loans", "لا توجد قروض")} desc={L("Loan and advance requests will appear here.", "ستظهر طلبات القروض والسلف هنا.")} /> : (
          <div className="tw">
            <table className="tbl cards" data-testid="loans-table">
              <thead><tr><th>{t("Employee")}</th><th>{t("Type")}</th><th className="end">{t("Amount")}</th><th className="end hide-md">{L("Instalment", "القسط")}</th><th className="hide-md">{L("Progress", "التقدم")}</th><th>{t("Status")}</th><th className="end">{t("Actions")}</th></tr></thead>
              <tbody>
                {list.map((l) => {
                  const e = emps.map.get(l.employeeId);
                  return (
                    <tr key={l.id} data-testid={"loan-row-" + l.id}>
                      <td className="k1"><Person e={e ?? unknownEmp(l.employeeId)} /></td>
                      <td className="x" data-l={t("Type")}>{typeLabel(l.type, isAr)}</td>
                      <td className="end k3" data-l={t("Amount")}><Money value={l.amount} dec={0} /></td>
                      <td className="end hide-md"><Money value={l.amount / l.months} dec={0} /> <span className="xs muted">× {l.months}</span></td>
                      <td className="hide-md" style={{ minWidth: 130 }}><Bar value={l.paid} max={l.months} tone={l.status === "Settled" ? "" : "gold"} /><span className="xs muted num">{l.paid}/{l.months}</span></td>
                      <td className="k2"><StatusChip status={l.status} /></td>
                      <td className="end k3" data-l="">
                        {l.status === "Pending" ? (can("requests.decide") && (
                          <div className="row nw" style={{ justifyContent: "flex-end" }}>
                            <Button variant="ghost sm" onClick={() => doDecide(l, false)} disabled={busy === l.id} data-testid={"btn-reject-" + l.id}>{t("Reject")}</Button>
                            <Button variant="sm" onClick={() => doDecide(l, true)} loading={busy === l.id} data-testid={"btn-approve-" + l.id}>{t("Approve")}</Button>
                          </div>
                        )) : (
                          <div className="row nw" style={{ justifyContent: "flex-end" }}>
                            <Button variant="ghost sm" onClick={() => setModal(l)} data-testid={"btn-schedule-" + l.id}>{L("Schedule", "الجدول")}</Button>
                            <IconButton icon="printer" label={L("Print voucher", "طباعة السند")} onClick={() => openPrint({ doc: "voucher", emp: l.employeeId })} data-testid={"btn-voucher-" + l.id} />
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {modal === "new" && <NewLoanModal onClose={() => setModal(null)} />}
      {modal && modal !== "new" && <ScheduleModal loan={modal} emp={emps.map.get(modal.employeeId)} onClose={() => setModal(null)} />}
    </>
  );
}
